import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { Select } from 'antd'
import axios from 'axios'
// import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear } from '../../../../../../../_metronic/helpers'
import { useSelectedWeek } from '../../../../../contexts/SelectedWeekContext'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear } from '../../../../../../_metronic/helpers'

const { Option } = Select

interface DataItem {
    week: string
    acteRate: number | null
    lowerCiBaselinePassageDiagNb: number | null
    upperCiBaselinePassageDiagNb: number | null
}
interface Trace {
    x: string[]
    y: (number | null)[]
    type: string
    mode: string
    marker?: { color: string }
    line?: { color: string }
    yaxis?: string
    name: string,
    hovertemplate: any | null
}
interface StreamgraphChartProps {
    season: SeasonRange
    selectedAgeClass: string[]
    setSelectedAgeClass: (v: any) => void
    selectedDiagnosisCategories: any
    setSelectedDiagnosisCategories: (v: any) => void
}
const VIRUS_LABELS = {
    FLU: "Influenza",
    "SARS-CoV-2": "SARS-CoV-2",
    RSV: "RSV",
    IRA: "Acute respiratory infections",
};
function getGraphTitle(virus: string): string {
    switch (virus) {
        case 'IRA':
            return 'Rate of SOS Médecins acts for acute respiratory infection among 100,000 SOS Médecins acts with a medical diagnosis'
        case 'FLU':
            return 'Rate of SOS Médecins acts for influenza/influenza like illness among 100,000 SOS Médecins acts with a medical diagnosis'
        case 'SARS-CoV-2':
            return 'Rate of SOS Médecins acts for suspected Covid-19 among 100,000 SOS Médecins acts with a medical diagnosis'
        case 'RSV':
            return 'Rate of SOS Médecins acts for bronchiolitis among 100,000 SOS Médecins acts with a medical diagnosis'
        default:
            return 'Rate of SOS Médecins acts among 100,000 SOS Médecins acts with a medical diagnosis'
    }
}
const DoctorSosRateGraph: React.FC<StreamgraphChartProps> = ({ season, selectedAgeClass, setSelectedAgeClass, selectedDiagnosisCategories, setSelectedDiagnosisCategories }) => {

    const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
    const [data, setData] = useState<DataItem[]>([])
    const [diagnosisCategories, setDiagnosisCategories] = useState([])
    const [ageClass, setAgeClass] = useState([])

    const [plotData, setPlotData] = useState<Trace[]>([])
    const [showArrow, setShowArrow] = useState<boolean>(false)
    //const [selectedVirus, setSelectedVirus] = useState('');

    const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
    const startYear = getYearFromWeek(season.startWeek)
    const endYear = getYearFromWeek(season.endWeek)
    const startWeekNumber = getWeekFromWeek(season.startWeek) + 1
    const isRSVSelected = selectedDiagnosisCategories === 'RSV'
    
    const API_URL = process.env.REACT_APP_API_URL
    function parseAgeClasses(ageClasses: string[]): string {
        const parsedString = ageClasses.map((age) => `ageClass%5B%5D=${age}`).join('&')
        return parsedString
    }
    const getVirusLabel = (value) => {
        return VIRUS_LABELS[value] || "";
    };
    function addMissingWeek(data: DataItem[]): DataItem[] {
        const startWeek = season.startWeek // First week in the data
        const endWeek = season.endWeek // Last week in the data
        const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
        const newData: DataItem[] = []

        let currentWeek = startWeek

        while (currentWeek !== endWeek) {
            // Move to the next week
            const [year, weekNum] = currentWeek.split('-W')
            const weekNumber = parseInt(weekNum)
            if (weekNumber < 52) {
                currentWeek = `${year}-W${(weekNumber + 1).toString()}`
            } else {
                currentWeek = `${parseInt(year) + 1}-W1`
            }
            if (!existingWeeks.has(currentWeek)) {
                newData.push({
                    week: currentWeek,
                    acteRate: null,
                    lowerCiBaselinePassageDiagNb: null,
                    upperCiBaselinePassageDiagNb: null,
                })
            }
        }

        // Find the index to insert the new week
        let insertionIndex = 0
        for (let i = 0; i < data.length; i++) {
            if (data[i].week <= `${startYear}-W52`) {
                insertionIndex = i + 1
            } else {
                break
            }
        }

        // Merge existing data with the newly added missing weeks
        const mergedData = [
            ...data.slice(0, insertionIndex),
            ...newData,
            ...data.slice(insertionIndex),
        ].sort((a, b) => {
            const aYear = parseInt(a.week.substring(0, 4), 10)
            const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
            const bYear = parseInt(b.week.substring(0, 4), 10)
            const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
            if (aYear !== bYear) {
                return aYear - bYear
            } else {
                return aWeek - bWeek
            }
        })

        return mergedData
    }

    const fetchData = async (
        ageClasses = selectedAgeClass,
        diagnosis = selectedDiagnosisCategories
    ) => {
        try {
            const traces: Trace[] = []

            // Fetch data for each age class and generate traces
            await Promise.all(
                ageClasses.map(async (ageClass, index) => {
                    // Pass index to get color from colors array
                    let filterAge = parseAgeClasses([ageClass])

                    const response = await axios.get(
                        API_URL +
                        `/doctorVisitsOdisse/list?${filterAge}&diagnosisCategory=${diagnosis}`
                    )
                    let data = addMissingWeek(response.data)
                    let readableAge
                    let selectedColor
                    switch (ageClass) {
                        case '[0 - 4] year(s)':
                            readableAge = '0 to 4 years'
                            selectedColor = '#33FF57'
                            break
                        case '[5 - 14] years':
                            readableAge = '5 to 14 years'
                            selectedColor = '#5733FF'
                            break
                        case '[15 - 64] years':
                            readableAge = '15 to 64 years'
                            selectedColor = '#FF33DD'
                            break
                        case '65 years and older':
                            readableAge = '65 years and older'
                            selectedColor = '#4B322C'
                            break
                        default:
                            readableAge = 'All ages'
                            selectedColor = '#203879'
                            break
                    }

                    const trace: Trace = {
                        x: data
                            .filter((entry) => {
                                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                                return (
                                    (currentYear === startYear && currentWeek >= startWeekNumber) ||
                                    (currentYear === endYear && currentWeek <= 27) ||
                                    (currentYear > startYear && currentYear < endYear)
                                );
                            })
                            .map((entry) => entry.week),
                        y: data
                            .filter((entry) => {
                                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                                // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                                return (
                                    (currentYear === startYear && currentWeek >= startWeekNumber) ||
                                    (currentYear === endYear && currentWeek <= 27) ||
                                    (currentYear > startYear && currentYear < endYear)

                                );
                            })
                            .map((entry) => entry.acteRate),
                        yaxis: 'y',
                        type: 'scatter',
                        mode: 'lines',
                        line: { color: selectedColor }, // Assign color based on index
                        name: `${readableAge} Rate - ${getVirusLabel(selectedDiagnosisCategories)}`, // Add age class to trace name
                        hovertemplate:
                            '<b>%{fullData.name}</b><br>' +
                            'Week: %{x}<br>' +
                            'Rate of SOS Médecins visits: %{y:.2f}<extra></extra>',
                    }

                    traces.push(trace)
                })
            )
            ageClasses.map(async (ageClass, index) => {
                // Pass index to get color from colors array
                let filterAge = parseAgeClasses([ageClass])

                const response = await axios.get(
                    API_URL +
                    `/doctorVisitsOdisse/list?${filterAge}&diagnosisCategory=${diagnosis}`
                )

                let endWeek = season?.endWeek?.split('-W')
                const alignedWeek =
                    startYear == seasonReferenceYear
                        ? selectedWeek
                        : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
                const selectWeek = alignedWeek.split('-W')
                const showArrow =
                    (Number(selectWeek[0]) > startYear ||
                        (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
                    (Number(selectWeek[0]) < Number(endWeek[0]) ||
                        (Number(selectWeek[0]) === Number(endWeek[0]) &&
                            Number(selectWeek[1]) <= Number(endWeek[1])))
                setShowArrow(showArrow)
                setSeasonSelectedWeek(alignedWeek);
            })
            const response = await axios.get(API_URL + `/doctorVisitsOdisse/list`)
            const startWeek = season.startWeek // First week in the data
            const endWeek = season.endWeek // Last week in the data
            const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
            const newData: DataItem[] = []
            const missingData: DataItem[] = []

            let currentWeek = startWeek

            while (currentWeek !== endWeek) {
                // Move to the next week
                const [year, weekNum] = currentWeek.split('-W')
                const weekNumber = parseInt(weekNum)
                if (weekNumber < 52) {
                    currentWeek = `${year}-W${(weekNumber + 1).toString()}`
                } else {
                    currentWeek = `${parseInt(year) + 1}-W1`
                }
                if (!existingWeeks.has(currentWeek)) {
                    missingData.push({
                        week: currentWeek,
                        acteRate: null,
                        lowerCiBaselinePassageDiagNb: null,
                        upperCiBaselinePassageDiagNb: null,
                    })
                }
            }


            const trace3: Trace = {
                x: missingData
                    .filter((entry) => {
                        const currentYear = parseInt(entry.week.substring(0, 4), 10)
                        const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                        // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                        return (
                            (currentYear === startYear && currentWeek >= startWeekNumber) ||
                            (currentYear === endYear && currentWeek <= 27) ||
                            (currentYear > startYear && currentYear < endYear)
                        );
                    })
                    .map((entry) => entry.week),
                y: missingData
                    .filter((entry) => {
                        const currentYear = parseInt(entry.week.substring(0, 4), 10)
                        const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                        // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
                        return (
                            (currentYear === startYear && currentWeek >= startWeekNumber) ||
                            (currentYear === endYear && currentWeek <= 27) ||
                            (currentYear > startYear && currentYear < endYear)
                        );
                    })
                    .map((entry) => 0),
                yaxis: 'y',
                type: 'scatter',
                mode: 'markers',
                marker: { color: '#000000' }, // Assign color based on index
                name: `Missing Data `, // Add age class to trace name
                hovertemplate:
                    '<b>%{fullData.name}</b><br>' +
                    'Week: %{x}<br>' +
                    '%{fullData.name}: %{y}<extra></extra>',
            }
            traces.push(trace3)
            setPlotData(traces)
            //console.log(response.data);
        } catch (error) {
            console.error('Error fetching data:', error)
            // Handle error or set a default value for data
            setData([])
        }
    }
    useEffect(() => {
        const listDiagnosisCategory = async () => {
            try {
                const response = await axios.get(
                    API_URL + '/doctorVisitsOdisse/listDiagnosisCategory'
                )
                const data = response.data.filter((category) => category !== 'GENERAL_RI')
                setDiagnosisCategories(data)
            } catch (error) {
                console.error('Error fetching data:', error)
                return null
            }
        }

        const listAgeClass = async () => {
            try {
                const response = await axios.get(
                    API_URL + '/doctorVisitsOdisse/listAgeClass'
                )
                const data = response.data
                setAgeClass(data)
            } catch (error) {
                console.error('Error fetching data:', error)
                return null
            }
        }
        listDiagnosisCategory()
        listAgeClass()
        fetchData(selectedAgeClass, selectedDiagnosisCategories)
    }, [selectedAgeClass, selectedDiagnosisCategories, selectedWeek])
    // Sample data

    const isMobile = window.innerWidth <= 1550;
    const layout = {
        title: getGraphTitle(selectedDiagnosisCategories),
        barmode: 'stack',
        bargap: 0.2, // Adjust the gap between bars
        bargroupgap: 0.1, // Adjust the gap between bar groups
        autosize: true,
        constrain: 'domain',
        annotations: [
            {
                xref: 'paper',
                yref: 'paper',
                x: -0.045,
                xanchor: isMobile ? 'right' : 'center',
                y: 0.5,
                yanchor: 'middle',
                text: 'Rate per 100,000',
                textangle: -90,    // rotation verticale
                font: {
                    size: 18,
                    color: '#203879',
                },
                showarrow: false,
            },
            {
                x: startYear == seasonReferenceYear ? selectedWeek : seasonSelectedWeek,
                y: 0,
                xref: 'x',
                yref: 'y',
                text: showArrow ? 'Selected Week' : '',
                font: {
                    family: 'Courier New, monospace',
                    size: 16,
                    color: '#CC1B1B',
                },
                arrowcolor: '#CC1B1B',
                showarrow: showArrow,
                arrowhead: 7,
                ax: 0,
                ay: -260,
            },
            // {
            //   xref: 'paper',
            //   yref: 'paper',
            //   x: 1.01,
            //   xanchor: 'left',
            //   y: 0.5,
            //   yanchor: 'bottom',
            //   text: 'min-max',
            //   font: {
            //     size: 12,

            //     color: '#BBBBBC',
            //   },
            //   showarrow: false,
            // },
        ],

        xaxis: {
            title: 'Weeks',
            autorange: true,
            type: 'category',
            tickangle: -45,
        },
        yaxis: {
            // title: {
            //   text: '#',
            //   orientation:'h',
            //   font: {
            //     size: 25,
            //     color: '#203879',
            //   },
            // },

            // autorange: true,
            range: [0, null],
            tickformat: 'd',
            exponentformat: 'none' //supprime K, M, etc.
        },
        // yaxis2: {
        //   titlefont: {color: 'rgb(148, 103, 189)'},
        //   tickfont: {color: 'rgb(148, 103, 189)'},
        //   overlaying: 'y',
        //   side: 'right',
        //   showgrid: false,
        //   zeroline: false,
        // },
        legend: {
            x: 0,
            y: -0.4, // Adjust the value to position the legend as per your requirement
            orientation: 'h', // 'h' for horizontal, 'v' for vertical
        },
    }

    function handleDataChangeAge(value): void {
        if (value.length === 0) {
            setSelectedAgeClass(['0-200'])
        } else {
            setSelectedAgeClass(value)
        }
    }

    const handleDataChangeVirus = (value) => {
        setSelectedDiagnosisCategories(value)
    }

    return (
        <>
            <div className='row'>
                 {
                    !isRSVSelected && <>
                    <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
                    Age group :
                </label>
                <div className='col-6'>
                    <Select
                        variant='borderless'
                        className='border border-gray-500 border-3 rounded'
                        style={{ width: '71%' }}
                        size='large'
                        value={selectedAgeClass}
                        onChange={(value) => handleDataChangeAge(value)}
                        mode='multiple'
                    >
                        <Option key={'0-200'} value={'0-200'}>
                            {'All ages'}
                        </Option>
                        <Option key={'[0 - 4] year(s)'} value={'[0 - 4] year(s)'}>
                            {'0 to 4 years'}
                        </Option>
                        <Option key={'[5 - 14] years'} value={'[5 - 14] years'}>
                            {'5 to 14 years'}
                        </Option>
                        <Option key={'[15 - 64] years'} value={'[15 - 64] years'}>
                            {'15 to 64 years'}
                        </Option>
                        <Option key={'65 years and older'} value={'65 years and older'}>
                            {'65 years and older'}
                        </Option>
                    </Select>
                </div>
                    </>
                 }
                <div className='row mt-2'>
                    <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
                        Virus :
                    </label>
                    <div className='col-6'>
                        <Select
                            variant='borderless'
                            className='border border-gray-500 border-3 rounded'
                            style={{ width: '72.5%' }}
                            size='large'
                            value={selectedDiagnosisCategories}
                            onChange={(value, option) => handleDataChangeVirus(value)}
                        >
                           <Option key={'IRA'} value={'IRA'}>
                                {'Acute respiratory infections'}
                            </Option>
                            <Option key={'FLU'} value={'FLU'}>
                                {'Influenza'}
                            </Option>
                            <Option key={'SARS-CoV-2'} value={'SARS-CoV-2'}>
                                {'SARS-CoV-2'}
                            </Option>
                            <Option key={'RSV'} value={'RSV'}>
                                {'RSV - less than 1 year old'}
                            </Option>
                        </Select>
                    </div>
                </div>
            </div>
            <div className='plotContainer'>
                <Plot
                    style={{ width: '100%', height: '500px' }}
                    data={plotData}
                    layout={layout}
                    config={{
                        displaylogo: false,
                        responsive: true,
                    }}
                />
            </div>
        </>
    )
}

export default DoctorSosRateGraph
